'use client';

import { ForwardedRef, useImperativeHandle, useState } from 'react';
import { IconButton, Stack, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  A11y,
  Controller,
  EffectFade,
  FreeMode,
  Mousewheel,
  Navigation,
  Pagination,
  Thumbs,
} from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { ProductImage } from './ProductImage';

import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/effect-fade';
import 'swiper/css/free-mode';
import 'swiper/css/mousewheel';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';

export interface ProductImagesProps {
  /**
   * Ref to the Swiper instance.
   */
  swiper?: ForwardedRef<SwiperClass | null | undefined>;

  /**
   * Ref to one or more Swiper instances that are controlled by this gallery.
   */
  controlled?: SwiperClass | SwiperClass[] | null;

  /**
   * The images to display in the gallery.
   */
  images: ProductImage[];

  /**
   * Whether the gallery is displayed in a modal.
   */
  modal?: boolean;

  /**
   * Callback to open the modal.
   */
  onOpenModal?(): void;

  className?: string;
}

const THUMBNAIL_SIZE = 68;

export function ProductImages({
  swiper,
  controlled,
  images,
  modal,
  onOpenModal,
  className,
}: ProductImagesProps) {
  const [imageSwiper, setImageSwiper] = useState<SwiperClass>();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass>();

  useImperativeHandle(swiper, () => imageSwiper, [imageSwiper]);

  const theme = useTheme();

  // Determine image size and direction based on responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const direction = isTablet || modal ? 'horizontal' : 'vertical';
  const imageSize = isMobile ? 200 : isTablet ? 400 : 600;

  const slidesPerView: number | 'auto' = 'auto';

  const showThumbs = true;
  const hideThumbsNavigation = false;
  const hideMainNavigation = true;

  return (
    <Stack
      flex={1}
      maxHeight={modal ? '100vh' : undefined}
      direction={direction === 'horizontal' ? 'column' : 'row-reverse'}
    >
      <ImageSwiper
        modules={[A11y, Controller, EffectFade, Navigation, Pagination, Thumbs]}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        effect="fade"
        direction={direction}
        imageSize={imageSize} // Pass imageSize as prop
        controller={{ control: controlled }}
        // while linking the thumbs swiper, we need to ensure it's not destroyed
        // .. this can happen in react strict mode if the thumbs swiper is unmounted
        thumbs={{ swiper: thumbsSwiper?.destroyed ? undefined : thumbsSwiper }}
        onSwiper={setImageSwiper}
        className={className}
      >
        {images.map((image, i) => (
          <SwiperSlide key={i} onClick={image.type === 'video' ? undefined : onOpenModal}>
            <ProductImage image={image} lazy={i > 0} />
          </SwiperSlide>
        ))}
        <NavigationButton hidden={hideMainNavigation} className="swiper-button swiper-button-next">
          <FontAwesomeIcon fixedWidth icon={icons.chevronRight} />
        </NavigationButton>
        <NavigationButton hidden={hideMainNavigation} className="swiper-button swiper-button-prev">
          <FontAwesomeIcon fixedWidth icon={icons.chevronLeft} />
        </NavigationButton>
      </ImageSwiper>
      {showThumbs && (
        <ThumbsStack direction={direction === 'horizontal' ? 'row' : 'column'}>
          <NavigationButton
            hidden={hideThumbsNavigation}
            className="swiper-button thumbs-button-prev"
          >
            <FontAwesomeIcon fixedWidth icon={icons.chevronLeft} />
          </NavigationButton>
          <ThumbsSwiper
            modules={[A11y, FreeMode, Mousewheel, Navigation, Thumbs]}
            slidesPerView={modal ? 'auto' : slidesPerView}
            spaceBetween={10}
            navigation={{ nextEl: '.thumbs-button-next', prevEl: '.thumbs-button-prev' }}
            mousewheel
            watchSlidesProgress
            direction={direction}
            imageSize={imageSize} // Pass imageSize as prop
            onSwiper={setThumbsSwiper}
          >
            {images.map((image, i) => (
              <ThumbsSlide key={i}>
                <ProductImage image={image} thumbnail />
              </ThumbsSlide>
            ))}
          </ThumbsSwiper>
          <NavigationButton
            hidden={hideThumbsNavigation}
            className="swiper-button thumbs-button-next"
          >
            <FontAwesomeIcon fixedWidth icon={icons.chevronRight} />
          </NavigationButton>
        </ThumbsStack>
      )}
    </Stack>
  );
}

interface CustomSwiperProps {
  direction?: 'horizontal' | 'vertical';
  imageSize?: number;
}

type ImageSwiperProps = React.ComponentProps<typeof Swiper> & CustomSwiperProps;
type ThumbsSwiperProps = React.ComponentProps<typeof Swiper> & CustomSwiperProps;

const ImageSwiper = styled(Swiper, {
  shouldForwardProp: prop => prop !== 'imageSize',
})<ImageSwiperProps>(({ theme, direction, imageSize }) => ({
  flex: 1,
  flexBasis: imageSize,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),

  width: direction === 'horizontal' ? '100%' : imageSize,
  height: direction === 'horizontal' ? `calc(100% - ${THUMBNAIL_SIZE + 16}px)` : imageSize,

  '&.gallery-swiper .swiper-slide-active': {
    height: imageSize,
  },

  '& .swiper-button': {
    position: 'absolute',
    width: 50,
    height: 50,

    display: 'flex',

    ':after': {
      content: '""',
    },

    '&.swiper-button-next': {
      left: 'auto',
      right: theme.spacing(1),
    },

    '&.swiper-button-prev': {
      left: theme.spacing(1),
      right: 'auto',
    },
  },

  '& .swiper-slide': {
    display: 'flex',
    backgroundColor: theme.palette.background.paper,

    '& img': {
      maxWidth: '100%',
      objectFit: 'contain',
      margin: 'auto',
    },
  },
}));

const ThumbsStack = styled(Stack)(({ theme, direction }) => ({
  alignItems: 'center',
  margin: theme.spacing(2),

  '& .swiper-button': {
    position: 'relative',
    transform: direction === 'column' ? 'rotate(90deg)' : undefined,
    width: 32,
    height: 32,

    display: 'flex',

    '&.swiper-button-disabled > svg': {
      color: theme.palette.action.disabled,
    },
  },
}));

const ThumbsSwiper = styled(Swiper, {
  shouldForwardProp: prop => prop !== 'imageSize',
})<ThumbsSwiperProps>(({ direction, imageSize }) => ({
  flex: 1,
  flexBasis: imageSize,

  // width shall either be the thumbnail size or the full width of the screen in large mode
  width: direction === 'horizontal' ? '100%' : THUMBNAIL_SIZE,
  height: direction === 'horizontal' ? THUMBNAIL_SIZE : imageSize,
}));

const ThumbsSlide = styled(SwiperSlide)(({ theme }) => ({
  boxSizing: 'border-box',

  // account for border
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,

  '&.swiper-slide img': {
    border: `2px solid transparent`,
    borderRadius: theme.shape.borderRadius,
  },

  '&.swiper-slide-thumb-active img': {
    border: `2px solid ${theme.palette.primary.main}`,
  },
}));

const NavigationButton = styled(IconButton)(({ hidden }) => ({
  display: hidden ? 'none !important' : 'block',
}));
